// CertificatesPage — Official funded account + reward certificates
// Per marketing: "Receive an official certificate for every funded account and every reward you earn"

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow } from '../../components/TfPrimitives';

interface Certificate {
  id: string;
  type: 'funded' | 'reward' | 'milestone';
  number: string;
  title: string;
  subtitle: string;
  amount?: number;
  tier: 'Beginner' | 'Mid' | 'Professional';
  account: string;
  issued: string;
  signature: string;
  verification: string;
}

const CERTS: Certificate[] = [
  { id: 'c1', type: 'funded',  number: 'FP-FND-2026-04821', title: 'Official Funded Trader Certificate', subtitle: 'Mid Tier · $100,000 Account',  tier: 'Mid',          account: '$100K', issued: '2026-07-04', signature: 'Sarah Chen · Head of Risk',     verification: 'funderpro.live/v/FP-FND-2026-04821' },
  { id: 'c2', type: 'reward',  number: 'FP-RWD-2026-01203', title: 'First Reward Certificate',          subtitle: 'Payout #1 · $1,240',            tier: 'Mid',          account: '$100K', issued: '2026-07-15', signature: 'Marcus Chen · Risk Manager',     verification: 'funderpro.live/v/FP-RWD-2026-01203', amount: 1240 },
  { id: 'c3', type: 'reward',  number: 'FP-RWD-2026-01489', title: 'Second Reward Certificate',         subtitle: 'Payout #2 · $2,180',            tier: 'Mid',          account: '$100K', issued: '2026-07-20', signature: 'Marcus Chen · Risk Manager',     verification: 'funderpro.live/v/FP-RWD-2026-01489', amount: 2180 },
  { id: 'c4', type: 'reward',  number: 'FP-RWD-2026-01642', title: 'Third Reward Certificate',          subtitle: 'Payout #3 · $1,890',            tier: 'Mid',          account: '$100K', issued: '2026-07-23', signature: 'Marcus Chen · Risk Manager',     verification: 'funderpro.live/v/FP-RWD-2026-01642', amount: 1890 },
  { id: 'c5', type: 'milestone', number: 'FP-MIL-2026-00001', title: 'First $10,000 Cumulative Payout',  subtitle: 'Across 4 payouts',                 tier: 'Mid',          account: '$100K', issued: '2026-07-23', signature: 'Sarah Chen · Head of Risk',     verification: 'funderpro.live/v/FP-MIL-2026-00001', amount: 10000 },
];

const TIER_COLOR = { Beginner: '#34C759', Mid: '#007AFF', Professional: '#1C1C1E' };

export function CertificatesPage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<'all' | 'funded' | 'reward' | 'milestone'>('all');
  const [view, setView] = useState<Certificate | null>(null);

  const filtered = CERTS.filter((c) => filter === 'all' || c.type === filter);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => view ? setView(null) : nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Certificates</div>
        <button onClick={() => alert('Share gallery')} className="px-3 text-[15px] text-tf-blue">Share</button>
      </div>

      {!view && (
        <>
          {/* Hero — white floating card with massive count + pill buttons */}
          <div className="mx-4 mt-4">
            <TfCardFloating className="text-center">
              <div className="text-[13px]" style={{ color: '#8E8E93' }}>Trophy case</div>
              <div className="mt-1 flex items-baseline justify-center gap-1">
                <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>{CERTS.length}</span>
                <span className="text-[15px] text-tf-text-3">certificates</span>
              </div>
              <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
                <span className="font-semibold text-tf-profit">+$5,310</span>
                <span className="text-tf-text-3">total payouts</span>
                <span className="text-tf-text-3">· Today</span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button onClick={() => alert('View gallery')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
                  Share
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 17L17 7" /><path d="M7 7h10v10" />
                  </svg>
                </button>
                <button onClick={() => alert('Download all')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
                  Download
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 7L7 17" /><path d="M17 17H7V7" />
                  </svg>
                </button>
              </div>
            </TfCardFloating>
          </div>

          {/* 3-up stat row */}
          <TfStatRow
            className="mt-4"
            stats={[
              { label: 'Funded', value: '1', color: '#34C759' },
              { label: 'Rewards', value: '3', color: '#FF9500' },
              { label: 'Milestones', value: '1', color: '#5856D6' },
            ]}
          />

          {/* Filters */}
          <div className="px-4 pt-4 flex items-center gap-1.5 overflow-x-auto">
            {(['all', 'funded', 'reward', 'milestone'] as const).map((f) => (
              <button
                key={f} onClick={() => setFilter(f)}
                data-testid={`cert-filter-${f}`}
                className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
                  filter === f ? 'bg-tf-text-1 text-white' : 'bg-white border border-tf-border text-tf-text-1 active:opacity-40'
                ].join(' ')}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Cert cards */}
          <div className="mx-4 mt-3 space-y-2.5">
            {filtered.map((c) => {
              const c1 = TIER_COLOR[c.tier];
              return (
                <button
                  key={c.id}
                  onClick={() => setView(c)}
                  data-testid={`cert-${c.id}`}
                  className="w-full rounded-[16px] bg-white p-4 text-left active:scale-[0.99] transition relative overflow-hidden"
                  style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}
                >
                  <div className="absolute top-2 right-2 text-[40px] opacity-10">{c.type === 'funded' ? '🏆' : c.type === 'reward' ? '💰' : '🎯'}</div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={['rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase',
                      c.type === 'funded' ? 'bg-tf-green-tint text-tf-profit' :
                      c.type === 'reward' ? 'bg-tf-orange-tint text-tf-warning' :
                      'bg-tf-purple-tint text-tf-info'
                    ].join(' ')}>{c.type}</span>
                    <span className="text-[10px] font-mono text-tf-text-3">{c.number}</span>
                  </div>
                  <h3 className="text-[15px] font-bold text-tf-text-1">{c.title}</h3>
                  <p className="text-[12px] text-tf-text-3 mt-0.5">{c.subtitle}</p>
                  <div className="mt-2 flex items-center justify-between text-[11px]">
                    <span className="text-tf-text-3">Issued {c.issued}</span>
                    {c.amount && <span className="font-bold text-tf-text-1">${c.amount.toLocaleString()}</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {view && <CertificateView cert={view} onBack={() => setView(null)} />}
    </div>
  );
}

function CertificateView({ cert, onBack }: { cert: Certificate; onBack: () => void }) {
  const nav = useNavigate();
  const c1 = TIER_COLOR[cert.tier];

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-4 flex flex-col items-center justify-center">
      <button onClick={onBack} className="self-start text-[12px] text-white/60 active:text-white mb-3">← Back to certificates</button>
      <div className="w-full max-w-md rounded-[20px] bg-white p-6 shadow-2xl relative overflow-hidden" data-testid="cert-detail">
        {/* Decorative corner */}
        <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ backgroundColor: c1, transform: 'translate(40%, -40%)' }} />
        <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-10" style={{ backgroundColor: c1, transform: 'translate(-30%, 30%)' }} />

        {/* Header */}
        <div className="text-center relative">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full text-[20px]" style={{ backgroundColor: c1 + '20' }}>🏆</div>
          <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-tf-text-3">Funder Pro · {cert.tier} Tier</div>
          <h1 className="mt-1 text-[24px] font-bold tracking-tight text-tf-text-1">{cert.title}</h1>
          <p className="mt-1 text-[12px] text-tf-text-3">{cert.subtitle}</p>
        </div>

        {/* Body */}
        <div className="my-6 space-y-2 text-center">
          {cert.amount && (
            <div className="text-[36px] font-bold tabular-nums" style={{ color: c1 }}>${cert.amount.toLocaleString()}</div>
          )}
          <div className="text-[11px] text-tf-text-3 uppercase tracking-wider">Issued</div>
          <div className="text-[14px] font-semibold text-tf-text-1">{cert.issued}</div>
        </div>

        {/* Signature */}
        <div className="border-t border-dashed border-tf-border pt-4 text-center">
          <div className="text-[18px] italic text-tf-text-1">✍ {cert.signature}</div>
          <div className="text-[10px] text-tf-text-3 mt-1">Authorized signatory</div>
        </div>

        {/* Verification */}
        <div className="mt-4 rounded-[10px] bg-tf-surface-1 p-2.5 text-center">
          <div className="text-[9px] uppercase tracking-wider text-tf-text-3 mb-1">Verify at</div>
          <div className="text-[11px] font-mono font-semibold text-tf-text-1">{cert.verification}</div>
        </div>

        <div className="mt-4 text-center text-[8px] uppercase tracking-wider text-tf-text-3">Certificate {cert.number}</div>
      </div>

      <div className="mt-4 flex gap-2 w-full max-w-md">
        <button onClick={() => alert('Downloaded PDF')} data-testid="cert-download" className="flex-1 h-11 rounded-full bg-white text-[14px] font-semibold text-tf-text-1 active:opacity-40">Download PDF</button>
        <button onClick={() => alert('Shared to socials')} data-testid="cert-share" className="flex-1 h-11 rounded-full bg-gradient-to-r from-tf-loss to-tf-orange text-[14px] font-bold text-white active:opacity-60">Share</button>
      </div>
    </div>
  );
}
